/* ui-drilldown.js — the full-screen drill-down overlay: adjustable filters,
   detail table, row overflow menu, raw-metadata viewer, CSV export, pagination. */
(function () {
  'use strict';
  const CAD = (window.CAD = window.CAD || {});

  const PAGE_SIZE = 50;

  const SENS_META = {
    high: { label: 'High', color: '#DC2626' },
    medium: { label: 'Medium', color: '#D97706' },
    low: { label: 'Low', color: '#0369A1' },
  };

  const CSV_COLUMNS = ['Activity Type', 'Filename', 'Category', 'Sensitivity', 'MIME Type', 'Size', 'Created Date', 'Downloadable'];

  let current = null; // { filters, modal }

  function esc(s) { return CAD.util.escapeHtml(s); }
  function icon(name, size) { return CAD.util.icon(name, size); }

  function catMeta(id) {
    const taxonomy = CAD.storage.getClassification() || { categories: [] };
    const map = CAD.charts.categoryMap(taxonomy);
    return map[id] || { id: id, label: id, icon: 'file-question', color: '#94A3B8' };
  }

  function badge(text, color) {
    return '<span class="badge" style="background:' + CAD.util.hexToRgba(color, 0.13) + ';color:' + CAD.util.darken(color, 0.62) + ';border-color:' + CAD.util.hexToRgba(color, 0.35) + '">' + esc(text) + '</span>';
  }

  // ---------- filtering ----------

  function getFilteredRows() {
    const f = current.filters;
    const range = CAD.ranges.resolve(f.rangeMode, f.customStart, f.customEnd);
    return CAD.storage.getFileList().filter(function (rec) {
      if (f.direction !== 'all' && rec.direction !== f.direction) return false;
      if (f.categoryId && rec.categoryId !== f.categoryId) return false;
      if (f.sensitivities.indexOf(rec.sensitivity) === -1) return false;
      const t = new Date(rec.createdAt).getTime();
      if (isNaN(t)) return false;
      if (range.start && t < range.start.getTime()) return false;
      return t <= range.end.getTime();
    });
  }

  // ---------- rendering ----------

  function filterBarHTML() {
    const f = current.filters;
    const taxonomy = CAD.storage.getClassification() || { categories: [] };
    const cats = CAD.charts.orderedCategories(taxonomy);

    return (
      '<div class="filter-bar">' +
      '  <div class="filter-group" role="group" aria-label="Sensitivity filter">' +
      '    <span class="filter-label">Sensitivity</span>' +
      ['high', 'medium', 'low'].map(function (s) {
        const active = f.sensitivities.indexOf(s) !== -1;
        return '<button class="chip sens-' + s + (active ? ' active' : '') + '" type="button" data-sens="' + s + '" aria-pressed="' + active + '">' + SENS_META[s].label + '</button>';
      }).join('') +
      '  </div>' +
      '  <div class="filter-group">' +
      '    <label class="filter-label" for="dd-category">Category</label>' +
      '    <select id="dd-category" class="select">' +
      '      <option value="">All categories</option>' +
      cats.map(function (c) { return '<option value="' + esc(c.id) + '"' + (f.categoryId === c.id ? ' selected' : '') + '>' + esc(c.label) + '</option>'; }).join('') +
      '    </select>' +
      '  </div>' +
      '  <div class="filter-group">' +
      '    <label class="filter-label" for="dd-activity">Activity type</label>' +
      '    <select id="dd-activity" class="select">' +
      '      <option value="all"' + (f.direction === 'all' ? ' selected' : '') + '>All</option>' +
      '      <option value="uploaded"' + (f.direction === 'uploaded' ? ' selected' : '') + '>Uploaded</option>' +
      '      <option value="generated"' + (f.direction === 'generated' ? ' selected' : '') + '>Generated</option>' +
      '    </select>' +
      '  </div>' +
      '  <div class="filter-group">' +
      '    <label class="filter-label" for="dd-range">Date range</label>' +
      '    <select id="dd-range" class="select">' +
      CAD.ranges.OPTIONS.map(function (o) { return '<option value="' + o.value + '"' + (f.rangeMode === o.value ? ' selected' : '') + '>' + o.label + '</option>'; }).join('') +
      '    </select>' +
      '  </div>' +
      '  <div class="filter-group custom' + (f.rangeMode === 'custom' ? '' : ' hidden') + '" id="dd-custom-range">' +
      '    <input type="date" id="dd-range-start" class="input" aria-label="Start date" value="' + esc(f.customStart || '') + '">' +
      '    <span class="range-sep">to</span>' +
      '    <input type="date" id="dd-range-end" class="input" aria-label="End date" value="' + esc(f.customEnd || '') + '">' +
      '  </div>' +
      '</div>'
    );
  }

  function tableHTML(rows, page) {
    const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
    const p = Math.min(Math.max(1, page), totalPages);
    const slice = rows.slice((p - 1) * PAGE_SIZE, p * PAGE_SIZE);

    if (rows.length === 0) {
      return (
        '<div class="empty-state">' +
        '  <p>' + icon('filter') + ' No files match these filters</p>' +
        '  <button class="btn-link" id="dd-clear-filters" type="button">Clear filters</button>' +
        '</div>'
      );
    }

    const body = slice.map(function (rec) {
      const cat = catMeta(rec.categoryId);
      const expired = rec.expiresAt && new Date(rec.expiresAt) < new Date();
      return (
        '<tr>' +
        '<td>' + badge(rec.direction === 'uploaded' ? 'Uploaded' : 'Generated', rec.direction === 'uploaded' ? '#2563EB' : '#7C3AED') + '</td>' +
        '<td class="cell-filename" title="' + esc(rec.filename) + '">' + esc(rec.filename) +
        (expired ? ' <span class="expired-badge" title="Expired on ' + esc(rec.expiresAt) + '">expired</span>' : '') + '</td>' +
        '<td>' + badge(cat.label, cat.color) + '</td>' +
        '<td>' + badge(SENS_META[rec.sensitivity] ? SENS_META[rec.sensitivity].label : rec.sensitivity, (SENS_META[rec.sensitivity] || {}).color || '#64748B') + '</td>' +
        '<td class="cell-mime">' + esc(rec.mimeType || '\u2014') + '</td>' +
        '<td class="cell-size">' + CAD.util.fmtBytes(rec.sizeBytes) + '</td>' +
        '<td class="cell-date">' + CAD.util.fmtDateTime(rec.createdAt) + '</td>' +
        '<td>' + (rec.downloadable ? '<span class="yes-no yes">yes</span>' : '<span class="yes-no">no</span>') + '</td>' +
        '<td class="cell-menu"><div class="row-menu">' +
        '  <button class="icon-btn row-menu-btn" type="button" aria-label="Row actions for ' + esc(rec.filename) + '" title="Row actions">' + icon('more-horizontal') + '</button>' +
        '  <div class="menu-pop hidden" role="menu">' +
        '    <button type="button" role="menuitem" data-action="copy-id" data-id="' + esc(rec.id) + '">' + icon('copy') + ' Copy file ID</button>' +
        '    <button type="button" role="menuitem" data-action="raw-meta" data-id="' + esc(rec.id) + '">' + icon('file-text') + ' View raw metadata JSON</button>' +
        '  </div>' +
        '</div></td>' +
        '</tr>'
      );
    }).join('');

    const from = (p - 1) * PAGE_SIZE + 1;
    const to = Math.min(rows.length, p * PAGE_SIZE);

    return (
      '<div class="table-wrap">' +
      '<table class="data-table">' +
      '  <thead><tr>' +
      '    <th>Activity Type</th><th>Filename</th><th>Category</th><th>Sensitivity</th>' +
      '    <th>MIME Type</th><th>Size</th><th>Created Date</th><th>Downloadable</th><th><span class="visually-hidden">Actions</span></th>' +
      '  </tr></thead>' +
      '  <tbody>' + body + '</tbody>' +
      '</table></div>' +
      '<div class="pagination">' +
      '  <span class="page-info">Showing ' + CAD.util.fmtNumber(from) + '\u2013' + CAD.util.fmtNumber(to) + ' of ' + CAD.util.fmtNumber(rows.length) + '</span>' +
      '  <div class="pager">' +
      '    <button class="icon-btn" id="dd-prev" type="button" aria-label="Previous page" ' + (p <= 1 ? 'disabled' : '') + '>' + icon('chevron-left') + '</button>' +
      '    <span class="page-ind">Page ' + p + ' of ' + totalPages + '</span>' +
      '    <button class="icon-btn" id="dd-next" type="button" aria-label="Next page" ' + (p >= totalPages ? 'disabled' : '') + '>' + icon('chevron-right') + '</button>' +
      '  </div>' +
      '</div>'
    );
  }

  function renderTable() {
    const rows = getFilteredRows();
    const zone = current.modal.el.querySelector('#dd-table-zone');
    // Render into a fresh child each time so listeners from the previous
    // render are discarded together with the old nodes (no accumulation).
    const inner = document.createElement('div');
    inner.innerHTML = tableHTML(rows, current.filters.page);
    zone.replaceChildren(inner);
    wireTable(inner, rows);
  }

  // ---------- wiring ----------

  function closeMenu() {
    document.querySelectorAll('.menu-pop:not(.hidden)').forEach(function (m) { m.classList.add('hidden'); });
  }

  function wireTable(zone, rows) {
    const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));

    zone.querySelectorAll('.row-menu-btn').forEach(function (btn) {
      btn.addEventListener('click', function (ev) {
        ev.stopPropagation();
        const pop = btn.parentElement.querySelector('.menu-pop');
        const wasOpen = !pop.classList.contains('hidden');
        closeMenu();
        if (!wasOpen) pop.classList.remove('hidden');
      });
    });

    zone.addEventListener('click', function (ev) {
      const item = ev.target.closest('.menu-pop button');
      if (!item) { closeMenu(); return; }
      const id = item.getAttribute('data-id');
      if (item.getAttribute('data-action') === 'copy-id') {
        CAD.util.copyText(id).then(function () { CAD.util.toast('File ID copied: ' + id); });
      } else {
        showRawMetadata(id);
      }
      closeMenu();
    });

    const prev = zone.querySelector('#dd-prev');
    const next = zone.querySelector('#dd-next');
    if (prev) prev.addEventListener('click', function () { current.filters.page--; renderTable(); });
    if (next) next.addEventListener('click', function () { current.filters.page++; renderTable(); });

    const clear = zone.querySelector('#dd-clear-filters');
    if (clear) clear.addEventListener('click', function () {
      current.filters.sensitivities = ['high', 'medium', 'low'];
      current.filters.categoryId = null;
      current.filters.direction = 'all';
      current.filters.page = 1;
      rerenderFilters();
      renderTable();
    });
  }

  function rerenderFilters() {
    current.modal.el.querySelector('.filter-bar').outerHTML = filterBarHTML();
    wireFilters();
  }

  function wireFilters() {
    const el = current.modal.el;

    el.querySelectorAll('[data-sens]').forEach(function (chip) {
      chip.addEventListener('click', function () {
        const s = chip.getAttribute('data-sens');
        const i = current.filters.sensitivities.indexOf(s);
        if (i === -1) current.filters.sensitivities.push(s);
        else if (current.filters.sensitivities.length > 1) current.filters.sensitivities.splice(i, 1); // keep at least one
        current.filters.page = 1;
        rerenderFilters();
        renderTable();
      });
    });

    el.querySelector('#dd-category').addEventListener('change', function () {
      current.filters.categoryId = this.value || null;
      current.filters.page = 1;
      renderTable();
    });
    el.querySelector('#dd-activity').addEventListener('change', function () {
      current.filters.direction = this.value;
      current.filters.page = 1;
      renderTable();
    });
    el.querySelector('#dd-range').addEventListener('change', function () {
      current.filters.rangeMode = this.value;
      current.filters.page = 1;
      el.querySelector('#dd-custom-range').classList.toggle('hidden', this.value !== 'custom');
      renderTable();
    });
    el.querySelector('#dd-range-start').addEventListener('change', function () {
      current.filters.customStart = this.value || null; current.filters.page = 1; renderTable();
    });
    el.querySelector('#dd-range-end').addEventListener('change', function () {
      current.filters.customEnd = this.value || null; current.filters.page = 1; renderTable();
    });
  }

  // ---------- raw metadata viewer ----------

  function showRawMetadata(id) {
    const rec = CAD.storage.getFiles()[id];
    if (!rec) return;
    CAD.ui.modal({
      title: 'Raw metadata \u2014 ' + rec.filename,
      className: 'meta-modal',
      content:
        '<pre class="meta-json mono">' + esc(JSON.stringify(rec, null, 2)) + '</pre>' +
        '<div class="confirm-actions"><button class="btn" id="meta-copy" type="button">' + icon('copy') + ' Copy JSON</button></div>',
      onOpen: function (m) {
        m.el.querySelector('#meta-copy').addEventListener('click', function () {
          CAD.util.copyText(JSON.stringify(rec, null, 2)).then(function () { CAD.util.toast('Metadata JSON copied.'); });
        });
      },
    });
  }

  // ---------- CSV export ----------

  function csvEscape(v) {
    const s = String(v == null ? '' : v);
    return /[",\n\r]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
  }

  function exportCSV() {
    const rows = getFilteredRows();
    if (rows.length === 0) { CAD.util.toast('Nothing to export under the current filters.', 'err'); return; }
    const lines = [CSV_COLUMNS.map(csvEscape).join(',')];
    rows.forEach(function (rec) {
      lines.push([
        rec.direction === 'uploaded' ? 'Uploaded' : 'Generated',
        rec.filename,
        catMeta(rec.categoryId).label,
        rec.sensitivity,
        rec.mimeType || '',
        CAD.util.fmtBytes(rec.sizeBytes),
        rec.createdAt,
        rec.downloadable ? 'yes' : 'no',
      ].map(csvEscape).join(','));
    });
    const stamp = new Date().toISOString().replace(/[:T]/g, '-').slice(0, 17);
    CAD.util.downloadText('claude-files-' + stamp + '.csv', lines.join('\r\n'), 'text/csv');
  }

  // ---------- open / close ----------

  function open(preset) {
    if (current) current.modal.close();

    const filters = {
      direction: preset.direction || 'all',
      categoryId: preset.categoryId || null,
      sensitivities: (preset.sensitivities && preset.sensitivities.length) ? preset.sensitivities.slice() : ['high', 'medium', 'low'],
      rangeMode: preset.rangeMode || '30',
      customStart: preset.customStart || null,
      customEnd: preset.customEnd || null,
      page: 1,
    };
    // Assign before building the modal content: filterBarHTML() reads current.filters.
    current = { filters: filters, modal: null };

    const modal = CAD.ui.modal({
      title: 'File activity details',
      className: 'drilldown',
      content:
        '<div class="filter-slot">' + filterBarHTML() + '</div>' +
        '<div id="dd-table-zone"></div>',
      onClose: function () { current = null; },
    });

    modal.el.querySelector('.overlay-head').insertAdjacentHTML('beforeend',
      '<button class="btn" id="dd-export" type="button">' + icon('download') + ' Export CSV</button>');

    current.modal = modal;

    wireFilters();
    modal.el.querySelector('#dd-export').addEventListener('click', exportCSV);
    renderTable();

    // Closing the drill-down leaves the dashboard untouched \u2014 its range
    // selection lives in CAD.dashboard.state, which we only ever copied from.
  }

  CAD.drilldown = { open: open };
})();
